<template>
  <section class="coupons-table-wrap">
      <!-- v-else -->
    <ve-table
      :columns="columns"
      scroll-width="140rem"
      :table-data="tableData"
      :border-around="true"
    />
    <empty-state v-if="!isLoading && showEmptyResult" :title="emptyMessage" />
    <div v-if="isLoading" class="campaign--loader">
      <spinner />
      <span>{{ $t('COUPON.COUPON_LIST.LOADING') }}</span>
    </div>
  </section>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import Label from 'dashboard/components/ui/Label';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName';
import timeMixin from 'dashboard/mixins/time';
import InboxName from 'dashboard/components/widgets/InboxName';
import { mapGetters } from 'vuex';
import responsiveMixin from 'shared/mixins/responsiveMixin.js';


export default {
  components: {
    EmptyState,
    Spinner,
    VeTable,
  },

  mixins: [clickaway, timeMixin, messageFormatterMixin,responsiveMixin],

  props: {
    coupons: {
      type: Array,
      default: () => [],
    },
    showEmptyResult: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data(){
    return {
      expand_id: null,
    }
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
    }),
    currentAccount(){
      return this.getAccount(this.currentUser.account_id);
    },
    emptyMessage() {
      return this.$t('COUPON.COUPON_LIST.EMPTY_MSG');
    },
    tableData() {
      if (this.isLoading) {
        return [];
      }
      return this.coupons;
    },
    columns() {

      return [
        {
          field: 'name',
          key: 'name',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.NAME'),
          align: 'left',
          fixed: this.isMobile ? false:'left',
          width: 300,
          renderHeaderCell: ({ column }) => (
            <div class="custom-header-title">
              <span class="font_regular font_500 text_14 capitalize" style="color:#fff;">{column.title}</span>
              <p class="font_regular font_500 text_10 capitalize" style="color:#fff;">{this.$t('COUPON.ADD_FORM.TYPE_FORM.TYPE.LABEL')}</p>
            </div>
          ),
          renderBodyCell: ({row})=>{
            return <div>
              <p class="font_regular font_500 text_14 capitalize word_break">{row.name}</p>
              <p class="font_regular font_500 text_10 text_black_secondary capitalize">{row.display_kind}</p>
            </div>
          },
        },
        {
          field: 'start_date',
          key: 'start_date',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.START_DATE'),
          align: 'left',
          width: 150,
          renderHeaderCell: ({ column }) => (
            <div class="custom-header-title">
              <span class="font_regular font_500 text_14 capitalize" style="color:#fff;">{this.$t('COUPON.COUPON_LIST.TABLE_HEADER.END_DATE')}</span>
              <p class="font_regular font_500 text_10 capitalize" style="color:#fff;">{column.title}</p>
            </div>
          ),
          renderBodyCell: ({row})=>{
            const expired_at = row.expired_at?.split('T')[0] || "-";
            const start_date = row.start_date?.split('T')[0] || "-";
            return <div>
              <p class="font_regular font_500 text_14 capitalize">{expired_at}</p>
              <p class="font_regular font_500 text_10 text_black_secondary capitalize">{start_date}</p>
            </div>
          },
        },
        {
          field: 'redeemed_type', // duration
          key: 'redeemed_type',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.DURATION'),
          align: 'left',
          width: 200,
          renderBodyCell: ({row})=>{
            // find for the redem type and show the details
            let info, occurance = '';
            if (row.redeemed_type === 'multiple_month') {
              info = 'Limited repeat every'
              occurance = `${row.extras?.limit_per_month || ''} / Month`
            }
            else if (row.redeemed_type === 'forever') {
              info = 'Limited no repeat';
              occurance = row.redeemed_type;
            }
            else if (row.redeemed_type === 'once') {
              info = 'Limited no repeat';
              occurance = row.redeemed_type;
            }

            return <div>
                <p class="font_regular font_500 text_14">{info}</p>
                <p class="font_regular font_500 text_10 ">{occurance}</p>
            </div>
          },
        },
        {
          field: 'status',
          key: 'status',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.STATUS'),
          align: 'left',
          width: 120,
          renderBodyCell: ({ row }) => {
            const labelText = row.status;
            const colorScheme =
              row.status === 'active'
              ? 'green_pro'
              : row.status === 'inactive'
              ? 'gray_pro'
              :  row.status === 'expired'
              ? 'warning_pro'
              :  row.status === 'limit_reached'
              ? 'primary_pro'
              : 'gray_pro';

            return <div class="coupon_table_cell">
              <Label title={labelText} colorScheme={colorScheme} />
            </div>;
          },
        },
        {
          field: 'discount_value',
          key: 'discount_value',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.DISCOUNT'),
          align: 'center',
          width: 130,
          renderBodyCell: ({row})=>{
            const postFix = row.discount_type === 'percentage' ? '%' : row.currency;
            return <div>
              <p class="font_regular font_500 text_14">{row.discount_value} {postFix}</p>
            </div>
          },
        },
        {
          field: 'issued',
          key: 'issued',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.ISSUE'),
          align: 'center',
          width: 120,
        },
        {
          field: 'redeemed',
          key: 'redeemed',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.REDEEM'),
          align: 'center',
          width: 120,
        },
        {
          field: 'sales',
          key: 'sales',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.SALES') + ` (${this.currentAccount?.currency})`,
          align: 'center',
          width: 120,
          renderBodyCell: ({row})=>{
            return <div>
              <p class="font_regular font_500 text_14">{row.sales}</p>
            </div>
          },
        },

        {
          field: 'action',
          key: 'action',
          title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.ACTION'),
          align: 'center',
          width: 150,
          fixed: this.isMobile ? false:'right',
          renderBodyCell: (row) => {
            return (
              <div class="button-wrapper">
                <WootButton
                  variant="clear"
                  icon="info"
                  color-scheme="secondary"
                  classNames="Xgrey-btn"
                  onClick={() => this.$emit('on-info-click', row)}
                >
                </WootButton>
                <WootButton
                  variant="clear"
                  icon="edit"
                  color-scheme="secondary"
                  classNames="Xgrey-btn"
                  onClick={() => this.$emit('on-edit-click', row)}
                >
                </WootButton>
                <WootButton
                  variant="link"
                  icon="dismiss-circle"
                  color-scheme="secondary"
                  onClick={() => this.$emit('on-delete-click', row)}
                >
                </WootButton>
              </div>
            )
          },
        },
        // {
        //   field: 'issue',
        //   key: 'issue',
        //   title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.ACTION'),
        //   align: 'left',
        // },

      ];
    },
  },
  methods:{},
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_fonts.scss';

.coupons-table-wrap::v-deep {
  .ve-table {
    padding-bottom: var(--space-large);
    .ve-table-container{
      overflow-y: hidden;
    }
    thead.ve-table-header .ve-table-header-tr{
      background-color: var(--w-500)
    }
    thead.ve-table-header .ve-table-header-tr .ve-table-header-th {
      font-size: var(--font-size-mini);
      padding: var(--space-small) var(--space-two);
    }
    tbody.ve-table-body .ve-table-body-tr .ve-table-body-td {
      @extend .font_regular;
      @extend .text_14;
      @extend .font_500;
      padding: var(--space-slab) var(--space-two);
      word-break: break-word;
      white-space: break-spaces;

      .inbox--name{
        @extend .font_regular;
        @extend .text_14;
        @extend .font_500;
        align-items: center;
      }
    }

  }

  .ve-table-header-th {
    background-color: var(--w-500) !important;
    color: var(--white) !important;
  }
  .ve-table-header-th:first-child {
    border-top-left-radius: 4px;
  }
  .ve-table-header-th:last-child {
    border-top-right-radius: 4px;
  }

  .row--title-block {
    align-items: center;
    display: flex;
    text-align: left;

    .title {
      font-size: var(--font-size-small);
      margin: 0;
      text-transform: capitalize;
    }
  }
  .label {
    padding: var(--space-smaller) var(--space-small);
  }
}

.campaign--loader {
  align-items: center;
  display: flex;
  font-size: var(--font-size-default);
  justify-content: center;
  padding: var(--space-big);
}

.button-wrapper {
  justify-content: space-evenly;
  display: flex;
  flex-direction: row;
  // min-width: 20rem;
}

.people_icon{
  position: absolute;
  width: 20px;
  height: 20px;
  top: 0;
  right: -20px;
  cursor: pointer;
  svg{
    @extend .text_black_secondary;
  }
}

</style>

<style scoped>
.coupons-table-wrap .coupon_table_cell .label{
  padding: 5px 12px;
  font-weight: 600;
}
</style>
